import React, { useState } from 'react';
import { observer } from 'mobx-react';

import { useParams } from 'react-router-dom';

import { Button } from 'components/Button';

import { ActivityType } from 'utils/stores/Activities/constants';

import DataError from 'containers/Error/DataError';

import { useStore } from 'utils/hooks/store';

import { useOnlineMonitor } from 'utils/hooks/useOnlineMonitor';

import { useLocalStorage } from 'utils/hooks/localStorage';

import SubmissionSection from 'containers/EntitiesFormSubmission/components/SubmissionSection';

import FormPreview from 'containers/EntitiesFormSubmission/components/FormPreview/FormPreview';

import SuccessPage from 'components/SuccessPage/SuccessPage';

import { CenteredCircularProgress } from 'components/Progress';

import { ensureServiceWorkerState } from 'serviceWorkerRegister';

import { timezoneStore } from 'stores/Timezone';

import EntityFormSubmittingModal from './components/EntityFormSubmittingModal';

import { Overview, Container, MainContainer, ButtonWrapper, SectionContainer, SubmissionContainer } from './styles';

import UserDetailsStep from './components/UserDetailsStep';
import CancelFlowModal from './components/CancelFlowModal';
import EntityFormPublicSubmissionActivity from './components/EntityFormPublicSubmissionActivity';
import PublicSubmissionHeader from './components/PublicSubmissionHeader';
import OfflineSuccessPage from './components/OfflineSuccessPage';
import DraftContinuationModal from './components/DraftContinuationModal/DraftContinuationModal';
import MissingFilesModal from './components/MissingFilesModal';

const PUBLIC_SUBMISSION_LOCAL_STORAGE_KEY = 'public_submission_user_details_';

const EntitiesFormPublicSubmission: React.FC = (): React.ReactElement => {
  const params = useParams<{ publicFormId: string }>();
  const isOnline = useOnlineMonitor();

  const {
    notification,
    entitiesFormSubmission: {
      data,
      isError,
      isLoading,
      showPreview,
      onNextClickOffline,
      onPreviousClickOffline,
      showConfirmPopup,
      submissionActivities,
      requiredNotFilled,
      displayReloadPage,
      displaySuccessPage,
      displayOfflineSuccessPage,
      loadPublicSubmission,
      setDisplayReloadPage,
      displayUserDetailsStep,
      getCurrentSectionProperties,
      publicSubmissionUserDetails,
      checkIfAllRequiredFieldsAreFilled,
      moveToFirstSectionFromUserDetails,
      showDraftContinuationModal,
      continueWithDraft,
      removeDraftAndStartNewSubmission,
      showMissingFilesModal,
      dismissMissingFilesModal,
      missingAttachmentFields,
    },
  } = useStore();

  const publicFormId = params.publicFormId as string;

  React.useEffect(() => {
    loadPublicSubmission(publicFormId);
  }, [publicFormId]);

  React.useEffect(() => {
    // This is a temporary flag, remove once we want to enable offline submission for every organisation
    ensureServiceWorkerState(data?.offlineSubmission);
  }, [data?.offlineSubmission]);

  React.useEffect(() => () => timezoneStore.clearPublicFlag(), []);

  const formName = data?.form.name || 'New Submission';

  // Use localStorage values if they exist, otherwise use empty strings
  const [storedDetails, setStoredDetails, removeStoredDetails] = useLocalStorage(
    `${PUBLIC_SUBMISSION_LOCAL_STORAGE_KEY}${publicFormId}`,
    {
      name: '',
      email: '',
      phone: '',
      rememberUserDetails: false,
    }
  );

  // Determine initial values from either localStorage or store
  const userDetails = {
    name: storedDetails.name || publicSubmissionUserDetails?.name || null,
    email: storedDetails.email || publicSubmissionUserDetails?.email || null,
    phone: storedDetails.phone || publicSubmissionUserDetails?.phone || null,
    rememberUserDetails: storedDetails.rememberUserDetails || false,
  };

  const [rememberUserDetails, setRememberUserDetails] = useState(userDetails.rememberUserDetails);
  const [openCancelFlowModal, setOpenCancelFlowModal] = useState(false);

  const onCancel = (): void => {
    if (data?.stack.length === 1) {
      setOpenCancelFlowModal(true);
    } else {
      onPreviousClickOffline();
    }
  };

  const handleContinueDraft = async (): Promise<void> => {
    await continueWithDraft();
  };

  const handleStartNew = async (): Promise<void> => {
    await removeDraftAndStartNewSubmission(publicFormId);
  };

  const onClickNext = (): void => {
    // handle the first step
    if (displayUserDetailsStep) {
      // check if all required fields are filled
      if (!checkIfAllRequiredFieldsAreFilled()) {
        notification.enqueueErrorSnackbar('Please fill in all required fields');
        return;
      }

      if (rememberUserDetails) {
        setStoredDetails({
          name: publicSubmissionUserDetails?.name || '',
          email: publicSubmissionUserDetails?.email || '',
          phone: publicSubmissionUserDetails?.phone || '',
          rememberUserDetails: true,
        });
      } else {
        removeStoredDetails();
      }
      moveToFirstSectionFromUserDetails();
    } else {
      onNextClickOffline();
    }
  };

  if (isError) {
    return (
      <MainContainer>
        <DataError />
      </MainContainer>
    );
  }

  if (isLoading || !data) {
    return (
      <MainContainer>
        <CenteredCircularProgress key="loading-progress" />
      </MainContainer>
    );
  }

  if (!getCurrentSectionProperties()) {
    return <DataError />;
  }

  if (displaySuccessPage) {
    return <SuccessPage message="Event reported successfully!" />;
  }

  if (displayOfflineSuccessPage) {
    return <OfflineSuccessPage />;
  }

  if (displayReloadPage) {
    return <SuccessPage message="Event form submission cancelled. Refresh the page to start a new submission." />;
  }

  if (showPreview) {
    return (
      <MainContainer>
        <PublicSubmissionHeader formName={formName} />
        <Overview>
          <FormPreview />

          {isOnline && <EntityFormPublicSubmissionActivity />}

          <EntityFormSubmittingModal
            open={showConfirmPopup}
            flowWithFile={submissionActivities?.activities?.filter((i) => i.type !== ActivityType.Message).length !== 0}
          />
          <MissingFilesModal
            open={showMissingFilesModal}
            onClose={dismissMissingFilesModal}
            missingFields={missingAttachmentFields}
          />
        </Overview>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <PublicSubmissionHeader formName={formName} />
      <Overview>
        <SubmissionContainer>
          {displayUserDetailsStep ? (
            <UserDetailsStep
              initialState={userDetails}
              rememberUserDetails={rememberUserDetails}
              onRemember={(value) => setRememberUserDetails(value)}
            />
          ) : (
            <SectionContainer>
              <SubmissionSection />
            </SectionContainer>
          )}
          <Container>
            <ButtonWrapper>
              <Button
                size="large"
                id="cancelButton"
                variant="outlined"
                onClick={onCancel}
                fullWidth={window.innerWidth <= 768}
              >
                {data.stack.length === 1 ? 'Cancel' : 'Back'}
              </Button>
              <Button
                size="large"
                id="submitButton"
                variant="contained"
                onClick={onClickNext}
                fullWidth={window.innerWidth <= 768}
                disabled={requiredNotFilled.length > 0}
              >
                Next
              </Button>
            </ButtonWrapper>
          </Container>
        </SubmissionContainer>
      </Overview>
      <CancelFlowModal
        open={openCancelFlowModal}
        onClose={() => setOpenCancelFlowModal(false)}
        onAction={() => setDisplayReloadPage(true)}
      />
      <DraftContinuationModal
        open={showDraftContinuationModal}
        onClose={handleStartNew}
        onContinueDraft={handleContinueDraft}
        onStartNew={handleStartNew}
      />
    </MainContainer>
  );
};

export default observer(EntitiesFormPublicSubmission);
